import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import services from '../../services';
import { type StoresState } from './storesTypes';
import {
  getInitialStoreValue,
  getReplacementServiceStoreId,
  type ServiceLocation,
} from '../../helpers/appHelper';
import { CONSOLIDATED_STORE_ID } from '../../services/appointment/model';
import {
  updateSelectedReplacementServiceStore,
  updateSelectedStore,
} from '../app/appReducer';
import { type RootState } from '../../store/store';

const initialState: StoresState = {
  availableStores: [],
  replacementServiceStores: [],
};

export const getStores = createAsyncThunk(
  'stores/getStores',
  async (
    {
      serviceProductId,
      serviceLocation,
      replacementServiceLocation,
    }: {
      serviceProductId: string;
      serviceLocation: ServiceLocation;
      replacementServiceLocation?: ServiceLocation;
    },
    { dispatch, getState, rejectWithValue },
  ) => {
    const rootState = getState() as RootState;
    const zipCode = rootState.search.postCode;
    const storeId = rootState.app.selected.storeId;

    const {
      result: { stores = [] },
      error,
    } = await services.appointment.getAvailableStores(
      zipCode,
      serviceProductId,
    );

    if (error) {
      return rejectWithValue(error);
    }

    if (replacementServiceLocation) {
      const selectedStoreId = getReplacementServiceStoreId(
        storeId,
        serviceProductId,
        replacementServiceLocation,
        stores,
        CSS_CONFIG,
      );
      if (selectedStoreId) {
        const selectedStore =
          stores.find((s) => s.value === selectedStoreId) || null;
        dispatch(
          updateSelectedReplacementServiceStore({
            store: selectedStore,
            storeId: selectedStoreId,
          }),
        );
      }
      return {
        stores,
        selectedStoreId,
        isReplacementServiceStore: true,
      };
    }

    let resolvedStoreId = storeId;
    if (
      stores.length &&
      storeId !== CONSOLIDATED_STORE_ID &&
      !stores.some((store) => store.value === storeId)
    ) {
      resolvedStoreId = getInitialStoreValue(
        serviceProductId,
        stores,
        serviceLocation,
        CSS_CONFIG,
      );
    }
    dispatch(updateSelectedStore(resolvedStoreId));

    return {
      stores,
      selectedStoreId: resolvedStoreId,
    };
  },
);

const storesSlice = createSlice({
  name: 'stores',
  initialState,
  reducers: {
    clearAvailableStores(state) {
      state.availableStores = [];
      state.replacementServiceStores = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getStores.fulfilled, (state, action) => {
      if (action.payload.isReplacementServiceStore) {
        state.replacementServiceStores = action.payload.stores;
      } else {
        state.availableStores = action.payload.stores;
      }
    });
  },
});

export const { clearAvailableStores } = storesSlice.actions;

export default storesSlice.reducer;
